import React, { useState } from 'react';
import { useContext } from 'react';
import { v4 } from 'uuid';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { TagContext } from '../../../../../context/TagContext';
import { CreateTagResponse, CreateTagVariables, TagMutations } from '../../../../../graphql/tag.graphql';
import useMutation from '../../../../../hooks/useMutation';
import { EntityType } from '../../../../../types/comment';
import { PromiseOr } from '../../../../../util/types.util';
import { Tag, TagInput } from '../../../../../types/tag';
import Icon, { IconType } from '../../../../icons/Icon';
import DropdownMultiSelect from '../../DropdownMultiSelect';
import DropdownItem from '../../parts/DropdownItem';
import { CompanyRoleAssignmentContext } from '../../../../../context/CompanyRoleAssignmentContext';

interface TagMultiSelect {
  selectedTagIds: Set<string>;
  onTagSelected: (tagId: string) => PromiseOr<void>;
  onTagRemoved: (tagId: string) => PromiseOr<void>;
  onTagCreated: (tag: Tag) => PromiseOr<void>;
  entityType: EntityType;
}

export default function TagMultiSelect({
  selectedTagIds,
  onTagSelected,
  onTagRemoved,
  onTagCreated,
  entityType,
}: TagMultiSelect) {
  const { currentCompany } = useContext(CompanyContext);
  const companyId = currentCompany.id;
  const { tags, setTags } = useContext(TagContext);
  const { isAdmin } = useContext(CompanyRoleAssignmentContext);

  const [queryReset, setQueryReset] = useState('');

  const selectableTagIds = [...tags.values()]
    .filter(t => t.type === entityType && !selectedTagIds.has(t.id))
    .map(t => t.id);

  const [createTag, { loading: createTagLoading }] = useMutation<CreateTagResponse, CreateTagVariables>(
    TagMutations.create,
    {
      onCompleted: response => {
        const createdTag = response.createTag[0];
        setTags(new Map(tags).set(createdTag.id, createdTag));
        onTagCreated(createdTag);
      },
    },
  );

  return (
    <DropdownMultiSelect<string>
      label={'Tags'}
      placeholder={'Select a tag'}
      queryReset={queryReset}
      items={selectableTagIds}
      values={new Set(selectedTagIds)}
      textBuilder={item => {
        const tag = tags.get(item);
        if (!tag) return 'Unknown tag';
        return tag.name;
      }}
      inputItemBuilder={item => {
        const tag = tags.get(item);
        if (!tag) return null;

        return (
          <div className='flex items-center gap-2 rounded-2 border p-1 text-sm' style={{ borderColor: tag.color }}>
            <p>{tag.name}</p>
            <Icon type={IconType.x} onClick={async () => await onTagRemoved(tag.id)} size={'size-4'} />
          </div>
        );
      }}
      lastBuilder={
        isAdmin
          ? (input, selected) => {
              if (!input) return null;

              return (
                <DropdownItem
                  key={v4()}
                  selected={selected}
                  onClick={async () => {
                    await createTag({
                      variables: {
                        tags: [
                          new TagInput({ companyId })
                            .withType(entityType)
                            .withName(input)
                            .withColor(Tag.getRandomColor())
                            .forCreate(),
                        ],
                      },
                    });
                    setQueryReset(v4());
                  }}
                >
                  <div>Create new tag: "{input}"</div>
                </DropdownItem>
              );
            }
          : undefined
      }
      onSelect={async item => await onTagSelected(item)}
    />
  );
}
